'use client';

import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

/**
 * Pairs a visualization with its underlying data table behind a compact
 * Chart|Table toggle pinned to the top-right of the card, on the same row as
 * the card title. BOTH panels are always rendered in the DOM — the inactive
 * one is hidden with the `hidden` attribute (display:none), never unmounted —
 * so the server-rendered table ships in the first-wave HTML and is crawlable
 * even while the chart tab is the default active view.
 *
 * The `chart` slot is typically an `ssr: false` recharts component (renders a
 * skeleton on the server); the `table` slot MUST be SSR-able (plain markup, no
 * browser-only APIs) so it appears in the initial HTML for search engines.
 * Both slots are expected to render a grid-variant Card whose header leaves its
 * right edge clear for the toggle.
 */
export function ChartTableTabs({
  chart,
  table,
  className,
}: {
  chart: React.ReactNode;
  table: React.ReactNode;
  className?: string;
}) {
  const { t } = useTranslation();
  const [view, setView] = useState<'chart' | 'table'>('chart');
  const baseId = useId();
  const chartPanelId = `${baseId}-chart`;
  const tablePanelId = `${baseId}-table`;

  const tabClass = (active: boolean) =>
    cn(
      'rounded px-1.5 py-0.5 text-[10px] font-medium transition-colors',
      active
        ? 'bg-background text-foreground shadow-sm'
        : 'text-muted-foreground hover:text-foreground',
    );

  return (
    <div className={cn('relative', className)}>
      <div
        role="tablist"
        aria-label={t('companyDetail.chartTableToggle.label')}
        className="absolute right-4 top-4 z-10 flex gap-0.5 rounded-md bg-muted/60 p-0.5"
      >
        <button
          type="button"
          role="tab"
          aria-selected={view === 'chart'}
          aria-controls={chartPanelId}
          className={tabClass(view === 'chart')}
          onClick={() => setView('chart')}
        >
          {t('companyDetail.chartTableToggle.chart')}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={view === 'table'}
          aria-controls={tablePanelId}
          className={tabClass(view === 'table')}
          onClick={() => setView('table')}
        >
          {t('companyDetail.chartTableToggle.table')}
        </button>
      </div>

      <div id={chartPanelId} role="tabpanel" hidden={view !== 'chart'}>
        {chart}
      </div>
      <div id={tablePanelId} role="tabpanel" hidden={view !== 'table'}>
        {table}
      </div>
    </div>
  );
}
